
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import tz from "dayjs/plugin/timezone";
import { mapGetters } from "vuex";
import {
	AD_LAYOUT_STUBS,
	AD_PLACEMENTS,
	INTERLINK_SPECIAL_CASE,
	PAGES,
} from "@consts";

dayjs.extend(utc);
dayjs.extend(tz);

export default {
	name: "WordsByLength",
	data() {
		return {
			AD_LAYOUT_STUBS,
			AD_PLACEMENTS,
		};
	},
	computed: {
		...mapGetters("navigation", [
			"isWordListDistributorPage",
			"isWordlistIndexPage",
			"isWordleLandingPage",
			"isQuordleLandingPage",
			"isHomePage",
		]),
		interlinkCase() {
			switch (this.$route.name) {
				case PAGES.WORDS_BY_LENGTH_LANDING_4:
					return INTERLINK_SPECIAL_CASE.LENGTH_4;
				case PAGES.WORDS_BY_LENGTH_LANDING_5:
					return INTERLINK_SPECIAL_CASE.LENGTH_5;
				case PAGES.WORDS_BY_LENGTH_LANDING_6:
					return INTERLINK_SPECIAL_CASE.LENGTH_6;
				case PAGES.WORDS_BY_LENGTH_LANDING_7:
					return INTERLINK_SPECIAL_CASE.LENGTH_7;
				case PAGES.WORDS_BY_LENGTH_LANDING_8:
					return INTERLINK_SPECIAL_CASE.LENGTH_8;
				default:
					return INTERLINK_SPECIAL_CASE.FALLBACK;
			}
		},
		headingText() {
			switch (this.$route.name) {
				case PAGES.WORDS_BY_LENGTH_LANDING_4:
					return {
						main: "4 Letter Word Finder & Unscrambler",
						subtitle:
							"Search, filter, and solve for 4-letter words",
					};

				case PAGES.WORDS_BY_LENGTH_LANDING_5:
					return {
						main: "5 Letter Word Finder & Unscrambler",
						subtitle:
							"Search, filter, and solve for 5-letter words",
					};
				case PAGES.WORDS_BY_LENGTH_LANDING_6:
					return {
						main: "6 Letter Word Finder & Unscrambler",
						subtitle:
							"Search, filter, and solve for 6-letter words",
					};
				case PAGES.WORDS_BY_LENGTH_LANDING_7:
					return {
						main: "7 Letter Word Finder & Unscrambler",
						subtitle:
							"Search, filter, and solve for 7-letter words",
					};
				case PAGES.WORDS_BY_LENGTH_LANDING_8:
					return {
						main: "8 Letter Word Finder & Unscrambler",
						subtitle:
							"Search, filter, and solve for 8-letter words",
					};
				default:
					return {
						main: "",
						subtitle: "",
					};
			}
		},
		content() {
			switch (this.$route.name) {
				case PAGES.WORDS_BY_LENGTH_LANDING_4:
					return [
						{
							heading: null,
							text: "<p> Four-letter words get a nasty rap, but when you need one, you really need one (and not just when you need to say a few choice words). We’re here to help with this handy tool! You can search and filter our list of words, making it easier to solve puzzles, play word games, and practice your vocabulary. </p>",
						},
						{
							heading: "How to Use Our 4-Letter Word Finder",
							text:
								"<p>Ready to make four-letter words from letters you know? It’s super easy. Here’s how:<p>" +
								"<ol>" +
								"<li>Enter any letters you happen to need to use. You can have the tool search for 4-letter words that contain, start with, or end with certain letters.</li>" +
								"<li>Click “Generate 4-Letter Word List” when you’re ready to see your results.</li>" +
								"<li>You’ll instantly get a list of 4-letter words using these letters. You can sort alphabetically or reverse alphabetically.</li>" +
								"</ol>",
						},
						{
							heading:
								"Searching for 4-Letter Words With These Letters",
							text:
								"<p>As you search, you may find that using specific letters can still give you a ton of words. If you only know one letter a word contains, you can end up getting a lot of results. For example, when it comes to words with “I,” 4-letter options are plentiful. In fact, there are over a thousand.</p>" +
								"<p>These tips can help you narrow things down when you have a lot of results:</p>" +
								"<ul> " +
								"<li> <strong>Try different placements for letters.</strong> Experiment with placing the letter you know in different parts of the <a href='https://wordfinder.yourdictionary.com/letter-words/4/'>4-letter word</a>. For instance, there are fewer than 100 4-letter words that start with “I.”</li>" +
								"<li> <strong>Add two filters together.</strong> Choose a letter that you want the word to contain, but then add another filter to narrow it down more. For example, there are only 52 words that start with “C” and contain “I.”</li>" +
								"<li> <strong>Think in combinations.</strong> Instead of thinking about a single letter you need to use, try making 4-letter words with letters in combination. If you specify that a word should contain “is,” our tool will return every 4-letter word with “is” in it. That’s 107 results.</li>" +
								"</ul>",
						},
						{
							heading: "Uses for Our 4-Letter Word Finder",
							text:
								"<p>From word games to vocab practice, there are tons of ways to use a four-letter word tool like ours. Here are some of our faves:</p>" +
								"<ul>" +
								"<li> <strong>Words With Friends</strong> Shorter words are a great way to start off your Words With Friends game, so a four-letter word is a good first play. We can also help you elevate your game with our <a href='https://wordfinder.yourdictionary.com/words-with-friends-cheat/'>Words With Friends helper</a>.</li>" +
								"<li> <strong>Scrabble</strong> Build just the right length of word based on what you have in your tray and what’s on the board. Our <a href='https://wordfinder.yourdictionary.com/scrabble-cheat/'>Scrabble cheat</a> can also come to the rescue when you’re stuck.</li>" +
								"<li> <strong>Crossword puzzles</strong> If you know one or more letters, you can narrow down the options by looking at all the words it could be and checking them against the clues. Use our <a href='https://wordfinder.yourdictionary.com/crosswords/'>crossword solver</a> to help even more.</li>" +
								"<li> <strong>Vocabulary practice</strong> When you’re teaching or learning about how letters work together to create words, it can be really helpful to have a list of short words using specific letters.</li>" +
								"</ul>",
						},
						{
							heading: "Popular 4-Letter Word Lists",
							text: "<p>Need some inspiration? These are some of our most popular 4-letter word lists:</p>",
						},
					];
				case PAGES.WORDS_BY_LENGTH_LANDING_5:
					return [
						{
							heading: null,
							text: "<p> Five-letter words are a pretty big deal, especially when it comes to playing word games like Wordle. Luckily, we’re here with a handy 5-letter word finder for Wordle and other games (plus vocabulary practice and more). It’s quick and easy too! </p>",
						},
						{
							heading: "How to Use Our 5-Letter Word Finder",
							text:
								"<p>This five-letter word finder is actually super easy to use. Just tell us what you know, and we’ll take care of the rest. Here’s how it works:<p>" +
								"<ol>" +
								"<li>Input the letters you know. You can search for 5-letter words with these letters. Place them where they are in the word (starts with, ends with, or contains).</li>" +
								"<li>Click “Generate 5-Letter Word List,” and let us get to work.</li>" +
								"<li>Instantly get a list of 5-letter words containing the letters you specified in the order you wanted them. You can sort your results alphabetically or reverse alphabetically.</li>" +
								"</ol>",
						},
						{
							heading: "Use Our Tool As a 5-Letter Wordle Solver",
							text:
								"<p>Our tool is one of the easiest ways to find 5-letter words for <a href='https://wordfinder.yourdictionary.com/letter-words/5/'>Wordle</a>. After all, every word in Wordle is five letters, so this is always going to be your go-to length. Once you know one or two of the letters, you’ll be able to narrow your results down a lot.</p>" +
								"<p>We can also help elevate your Wordle game in other ways! Try our other handy tools too:</p>" +
								"<ul> " +
								"<li> <a href='https://wordfinder.yourdictionary.com/wordle/'>Wordle Solver</a> - Specifically designed for Wordle, this solver lets you input letters in a specific order.</li>" +
								"<li> <a href='https://wordfinder.yourdictionary.com/wordle/hints/'>Wordle Hints</a> - Get hints and answers to help you solve today’s Wordle without giving too much away.</li>" +
								"<li> <a href='https://wordfinder.yourdictionary.com/wordle/answers/'>Past Wordle Answers</a> - Take a look at the past Wordle answers to check your work and satisfy your curiosity. You can get <a href='https://wordfinder.yourdictionary.com/wordle/yesterday-hints/'> hints and answers for yesterday’s Wordle</a> too.</li>" +
								"</ul>",
						},
						{
							heading:
								"Searching for 5-Letter Words With These Letters",
							text:
								"<p>Using only one specific letter can still give you a ton of words. For example, there are actually over 5,000 five-letter words that contain “A.” Don’t stress, though. These tips can help you narrow things down when you have a lot of results:</p>" +
								"<ul>" +
								"<li> <strong>Pick your placement.</strong> Try placing the letter you know in different parts of the word. For example, there are fewer than 1,000 5-letter words that start with “A.” If you’re looking for 5-letter words for Wordle, chances are good you already know a bit about where the letter falls in the word. </li>" +
								"<li> <strong>Combine filters.</strong> You can do more than choose a letter that you want the word to contain. Add a second filter for “starts with” or “ends with” to give you more specific results. For example, there are only 251 5-letter words that start with “L” and contain “A.” </li>" +
								"<li> <strong>Try some combos.</strong> Make 5-letter words with letters in combination instead of just searching for one letter. If you tell our tool that a word should contain “ate,” you’ll get every 5-letter word with “ate” in it. That’s 71 words. </li>" +
								"</ul>",
						},
						{
							heading: "More Uses for Our 5-Letter Word Finder",
							text:
								"<p>Wordle might be tops, but there are tons of other ways to use a five-letter word tool like ours. Here are some of our faves:</p>" +
								"<ul>" +
								"<li> <strong>Crossword puzzles</strong> - Stumped? Don’t worry. If you know one or more letters in your five-letter word, you can see all the options for what it could be. Then just check them against the clues. Hint: use our <a href='https://wordfinder.yourdictionary.com/crosswords/'>crossword solver</a> for even more help. </li>" +
								"<li> <strong>Scrabble</strong> - Need to make a play based on what you have in your tray and what’s on the board? We can help. And our <a href='https://wordfinder.yourdictionary.com/scrabble-cheat/'>Scrabble cheat</a> is another great option. </li>" +
								"<li> <strong>Words With Friends</strong> - A five-letter word is a great way to start off your Words With Friends game. We can also give you a win with our <a href='https://wordfinder.yourdictionary.com/words-with-friends-cheat/'>Words With Friends helper</a>. </li>" +
								"<li> <strong>Vocab practice</strong> - - Teach or learn about how letters work together to create words. For instance, you can create every 5-letter word containing a certain sound or letter. </li>" +
								"</ul>",
						},
						{
							heading: "Popular 5-Letter Word Lists",
							text: "<p>Ready for more? Here are some of our most popular 5-letter word lists:</p>",
						},
					];
				case PAGES.WORDS_BY_LENGTH_LANDING_6:
					return [
						{
							heading: null,
							text: "<p> Six-letter words come in handy when you’re playing Words With Friends, dominating on the Scrabble board, or just improving your vocab. Our six-letter word finder is designed by our team of word game enthusiasts, so you’re going to love using it. You can search and filter, and it’s easy to customize the list the way you need to. </p>",
						},
						{
							heading: "How to Use Our 6-Letter Word Finder",
							text:
								"<p>Ready to make 6-letter words from the letters you know? It’s totally easy. Here’s what to do:<p>" +
								"<ol>" +
								"<li><strong>Input your letters.</strong>Enter the letters you want to use. We’ll search for 6-letter words that contain, start with, or end with certain letters.</li>" +
								"<li><strong>Generate your 6-letter word list.</strong>Click “Generate 6-Letter Word List” and watch the results instantly appear.</li>" +
								"<li><strong>Sort your list.</strong>Check out your list of 6-letter words with these letters. You can sort alphabetically or reverse alphabetically.</li>" +
								"</ol>",
						},
						{
							heading:
								"Searching for 6-Letter Words With These Letters",
							text:
								"<p>A simple search can return a lot of words that have six letters, so it really helps to narrow things down a bit. For example, there are actually over 700 6-letter words using “Z,” even though that may seem like a rare letter. </p>" +
								"<p>Keep these tips in mind to refine your results:</p>" +
								"<ul> " +
								"<li> <strong>Switch up the placement.</strong> Try placing the letter you know in different parts of the <a href='https://wordfinder.yourdictionary.com/letter-words/6/?dictionary=WWF'>6-letter word</a>. For instance, there are only 160 6-letter words that start with “Z.”</li>" +
								"<li> <strong>Create a filter combo.</strong> Use more than one filter at once. For example, there are only 69 words that start with “Z” and contain “O.”</li>" +
								"<li> <strong>Search for combinations.</strong> Rather than using just one letter, try making 6-letter words with letters in combination. If you search for a 6-letter word that contains “OZ,” our tool will return every 6-letter word with these letters in this order. That’s 73 results.</li>" +
								"</ul>",
						},
						{
							heading: "Uses for Our 6-Letter Word Finder",
							text:
								"<p>From your favorite word games to your school work, there are lots of ways to use a handy six-letter word tool like this. Here are just a few of our faves:</p>" +
								"<ul>" +
								"<li> <strong>Words With Friends</strong> - Ready to bring in a longer word? Six letters is a great Words With Friends play. We’re also here to make your game more fun with our <a href='https://wordfinder.yourdictionary.com/words-with-friends-cheat/'>Words With Friends helper</a>.</li>" +
								"<li> <strong>Scrabble</strong> - What’s in your tray? Make a 6-letter word with these letters and a little help from us. Our <a href='https://wordfinder.yourdictionary.com/scrabble-cheat/'>Scrabble cheat</a> can also come in handy.</li>" +
								"<li> <strong>Crossword puzzles</strong> - Know some of the letters for that tricky puzzle? We can help. Use our <a href='https://wordfinder.yourdictionary.com/crosswords/'>crossword solver</a> to make things even easier.</li>" +
								"<li> <strong>Vocabulary practice</strong> - If you’re learning new words or how to use letters in combination, our tool is a life-saver.</li>" +
								"</ul>",
						},
						{
							heading: "Popular 6-Letter Word Lists",
							text: "<p>Need some inspiration? These are some of our most popular 6-letter word lists:</p>",
						},
					];
				case PAGES.WORDS_BY_LENGTH_LANDING_7:
					return [
						{
							heading: null,
							text: "<p> Seven-letter words come in handy all the time, and we’re here to help you find just the ones you need. Make that longer word in Words With Friends, use all your tiles in your first play on the Scrabble board, or just give your vocab an upgrade. </br></br> Our team of word game enthusiasts has designed this seven-letter word finder with people like you in mind. It’s easy to search and filter, and you can even customize the list for your precise needs. </p>",
						},
						{
							heading: "How to Use Our 7-Letter Word Finder",
							text:
								"<p>Ready to unscramble 7-letter words or create new words from the letters you know? We’ve made it really easy. Here’s how:<p>" +
								"<ol>" +
								"<li><strong>Type in your letters.</strong> Input the letters you want to use. We’ll search for 7-letter words with these letters. Pick whether you want them to contain, start with, or end with the letters you pick.</li>" +
								"<li><strong>Generate 7-letter words.</strong> Click “Generate 7-Letter Word List.” Your results will instantly appear.</li>" +
								"<li><strong>Sort your words.</strong> Take a look at your list of every 7-letter word using these letters. You can sort alphabetically or reverse alphabetically.</li>" +
								"</ol>",
						},
						{
							heading:
								"Searching for 7-Letter Words With These Letters",
							text:
								"<p>A basic search can give you a ton of seven-letter words, so it’s handy to narrow down your search. For instance, there are over 22,000 7-letter words using “E.” That’s a lot to sort through.</p>" +
								"<p>Refining your results is easy, though. Just keep these tips in mind:</p>" +
								"<ul> " +
								"<li> <strong>Move the letter around.</strong> Try a search with the letter you want in different parts of the <a href='https://wordfinder.yourdictionary.com/letter-words/7/?dictionary=WWF'>7-letter word</a>. For example, there are only a little over 1,200 7-letter words that start with “E.”</li>" +
								"<li> <strong>Combine filters.</strong> You can stack filters for a really good search too. For instance, there are only 368 7-letter words that start with “E” and contain “L.”</li>" +
								"<li> <strong>Look for letter combinations.</strong> Think beyond a single letter and try combinations. You’ll get only 78 results if you search for seven-letter words with the following letters: “DEL.” Add another letter, and it’s even fewer. There are only 8 seven-letter words with “DELA.”</li>" +
								"</ul>",
						},
						{
							heading: "Uses for Our 7-Letter Word Finder",
							text:
								"<p>There are tons of great uses for our seven-letter word finder. From schoolwork to fun word games, these are some of our faves:</p>" +
								"<ul>" +
								"<li> <strong>Words With Friends</strong> - Make a power play with a longer word. While you’re at it, also try our <a href='https://wordfinder.yourdictionary.com/words-with-friends-cheat/'>Words With Friends helper</a>.</li>" +
								"<li> <strong>Word scrambles</strong> - Stumped by mixed-up letters? Don’t stress. If you have a scrambled 7-letter word, unscramble it with our tool.</li>" +
								"<li> <strong>Scrabble</strong> - How many letters in your tray? Use them all on your first play by entering them into our 7-letter word finder. Our <a href='https://wordfinder.yourdictionary.com/scrabble-cheat/'>Scrabble cheat</a> can also help a ton.</li>" +
								"<li> <strong>Crossword puzzles</strong> - If you have a seven-letter word in your crossword and only know some of the letters, we can help. You can also elevate your puzzle experience with our <a href='https://wordfinder.yourdictionary.com/crosswords/'>crossword solver</a>.</li>" +
								"<li> <strong>Vocabulary practice</strong> - Enter your letters to find every 7-letter word with these letters. It’s a great way to see how words are related to each other.</li>" +
								"</ul>",
						},
						{
							heading: "Popular 7-Letter Word Lists",
							text: "<p>Need some inspiration? These are some of our most popular 7-letter word lists:</p>",
						},
					];
				case PAGES.WORDS_BY_LENGTH_LANDING_8:
					return [
						{
							heading: null,
							text: "<p> Take the mystery out of eight-letter words with our handy unscrambler. Use every tile in your Scrabble rack to build on what’s already on the board or make a longer play in Words With Friends. There are so many possibilities! </br></br> Designed by our team of word game enthusiasts to be super useful and user friendly, this 8-letter word finder makes short work of longer words. Pick your letters and where they go, and get instant results. </p>",
						},
						{
							heading: "How to Use Our 8-Letter Word Finder",
							text:
								"<p>Ready to find 8-letter words using the letters you want? It really is easy. Here’s how:<p>" +
								"<ol>" +
								"<li><strong>Input your letters.</strong> Start by entering the letters you need to use. Search for “contains” to get words with 8 letters containing the letter you want. Try “ends” or “starts” to put your letter at the beginning or end of the word.</li>" +
								"<li><strong>Instantly get 8-letter words.</strong> Click “Generate 8-Letter Word List” to see the results of your search.</li>" +
								"<li><strong>Sort your results.</strong> Take a look at your list and sort alphabetically or reverse alphabetically.</li>" +
								"</ol>",
						},
						{
							heading:
								"Searching for 8-Letter Words With These Letters",
							text:
								"<p>Searching for eight-letter English words with only one specific letter can return a ton of results, so knowing how to narrow things down can be a big help. For instance, there are over 16,000 8-letter words using “A.” That can be a little overwhelming.</p>" +
								"<p>Keep these tips in mind to refine your search:</p>" +
								"<ul> " +
								"<li><strong>Choose where you want the letter.</strong> Try searching for a letter in a specific spot in the <a href='https://wordfinder.yourdictionary.com/letter-words/8/?dictionary=WWF'>8-letter word</a>. For example, there are only 1,765 eight-letter “A” words, so it helps to search for a word that starts with the letter.</li>" +
								"<li><strong>Create a filter combo.</strong> Combine multiple filters to give you an even more targeted search. For instance, there are only 555 8-letter words that start with “L” and contain “A.”</li>" +
								"<li><strong>Make letter combinations.</strong> With a longer word, it’s all about combinations. Try searching for several letters together. If you search for “ALT,” you’ll get only 83 8-letter words using this combination.</li>" +
								"</ul>",
						},
						{
							heading: "Uses for Our 8-Letter Word Finder",
							text:
								"<p>From word games to work, there are a lot of ways to use our eight-letter word finder:</p>" +
								"<ul>" +
								"<li><strong>Words With Friends</strong> - Play a long word and dominate the game. Don’t forget to try our <a href='https://wordfinder.yourdictionary.com/words-with-friends-cheat/'>Words With Friends helper</a> too.</li>" +
								"<li><strong>Word scrambles</strong> - Don’t let scrambled letters stump you. Just input the letters you know to see all the possibilities.</li>" +
								"<li><strong>Scrabble</strong> - Build off of any letter on the board and use all seven of your tiles at the same time. Our <a href='https://wordfinder.yourdictionary.com/scrabble-cheat/'>Scrabble cheat</a> can also come in handy.</li>" +
								"<li><strong>Crossword puzzles</strong> - If you have an eight-letter word you can’t guess, don’t stress! Just put in the letters you know. Try our <a href='https://wordfinder.yourdictionary.com/crosswords/'>crossword solver</a> too.</li>" +
								"<li><strong>Vocabulary practice</strong> - See how many words you can make with eight letters. Create word searches and training games too.</li>" +
								"</ul>",
						},

						{
							heading: "Popular 8-Letter Word Lists",
							text: "<p>Need some inspiration? These are some of our most popular 8-letter word lists:</p>",
						},
					];
				default:
					return [
						{
							heading: "",
							text: "",
						},
					];
			}
		},
	},
	methods: {},
};
